import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { emptySession } from '../mock';
import { computeScores } from '../lib/scoring';
import { ScorePanel } from '../components/ScorePanel';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, ArrowRight, Save, Check, ChevronDown, BarChart3 } from 'lucide-react';
import {
  Step1Profile, Step2NetWorth, Step3Income, Step4Risk, Step5Goals,
  Step6Insurance, Step7Retirement, Step8Tax, Step9Emergency
} from './MRISteps';
import ConversationLog from '../components/ConversationLog';
import PrefillLinkButton from '../components/PrefillLinkButton';
import { ROLES } from '../mock';

const STEPS = [
  { key: 'profile', label: 'Profile' },
  { key: 'networth', label: 'Net Worth' },
  { key: 'income', label: 'Income' },
  { key: 'risk', label: 'Risk' },
  { key: 'goals', label: 'Goals' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'retirement', label: 'Retirement' },
  { key: 'tax', label: 'Tax' },
  { key: 'emergency', label: 'Health' }
];

const MRIWizardPage = () => {
  const { clientId, sessionId } = useParams();
  const nav = useNavigate();
  const { getClient, getSession, createSession, patchSession, user } = useApp();

  const client = getClient(clientId);
  const existing = sessionId && sessionId !== 'new' ? getSession(sessionId) : null;
  const canEdit = user?.role === ROLES.RM && (!existing || existing.rm_id === user.user_id || !existing.rm_id);

  const [session, setSession] = React.useState(() => {
    if (existing) return existing;
    const s = emptySession();
    s.client_id = clientId;
    s.rm_id = user?.user_id;
    if (client) { s.name = client.name; s.city = client.city; }
    return s;
  });
  const [step, setStep] = React.useState(0);
  const [savedAt, setSavedAt] = React.useState(null);
  const persistedIdRef = React.useRef(existing?.id || null);

  const persist = React.useCallback(async (patch, statusOverride) => {
    if (!canEdit) return null;
    const payload = { ...session, ...patch };
    if (statusOverride) payload.status = statusOverride;
    // Strip fields the backend does not want on create/update
    const body = { ...payload };
    delete body.id; delete body.rm_id; delete body.created_at; delete body.updated_at;
    delete body.conversation_log; delete body.signed_report;
    try {
      let saved;
      if (persistedIdRef.current) {
        saved = await patchSession(persistedIdRef.current, body);
      } else {
        saved = await createSession(body);
        persistedIdRef.current = saved.id;
      }
      setSession(saved);
      setSavedAt(new Date());
      return saved;
    } catch (e) {
      toast({ title: 'Save failed', description: e?.apiMessage || 'Unable to save session' });
      return null;
    }
  }, [session, canEdit, createSession, patchSession]);

  // Auto-persist new session on mount so conversation log can work immediately
  // Also update URL to use the real session ID after creation
  React.useEffect(() => {
    if (!canEdit || persistedIdRef.current) return;
    const isNewSession = !sessionId || sessionId === 'new';
    persist({}).then((saved) => {
      if (saved && isNewSession) {
        nav(`/clients/${clientId}/mri/${saved.id}`, { replace: true });
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save every 30s (only for RM editing)
  React.useEffect(() => {
    if (!canEdit) return;
    const t = setInterval(() => { persist({}); }, 30000);
    return () => clearInterval(t);
  }, [persist, canEdit]);

  const set = React.useCallback((k, v) => {
    if (!canEdit) return;
    setSession((prev) => ({ ...prev, [k]: v }));
  }, [canEdit]);

  const scores = React.useMemo(() => computeScores(session), [session]);

  const handleSaveDraft = async () => {
    const s = await persist({}, 'draft');
    if (s) toast({ title: 'Draft saved', description: 'You can resume later.' });
  };

  const handleComplete = async () => {
    const s = await persist({}, 'completed');
    if (s) {
      toast({ title: 'MRI session completed', description: 'Report is now available.' });
      nav(`/clients/${clientId}/mri/${s.id}/report`);
    }
  };

  const goNext = () => step < STEPS.length - 1 ? setStep(step + 1) : handleComplete();
  const goBack = () => step > 0 && setStep(step - 1);

  const renderStep = () => {
    switch (step) {
      case 0: return <Step1Profile s={session} set={set} />;
      case 1: return <Step2NetWorth s={session} set={set} />;
      case 2: return <Step3Income s={session} set={set} />;
      case 3: return <Step4Risk s={session} set={set} />;
      case 4: return <Step5Goals s={session} set={set} />;
      case 5: return <Step6Insurance s={session} set={set} />;
      case 6: return <Step7Retirement s={session} set={set} />;
      case 7: return <Step8Tax s={session} set={set} />;
      case 8: return <Step9Emergency s={session} set={set} />;
      default: return null;
    }
  };

  if (!client) {
    return <div className="p-8 text-center text-brand-indigo">Client not found. <Button onClick={() => nav('/clients')} className="ml-2">Back</Button></div>;
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-[1500px] mx-auto">
      <button onClick={() => nav(`/clients/${clientId}`)} className="text-xs text-brand-indigo font-semibold flex items-center gap-1 hover:underline mb-3">
        <ArrowLeft className="h-3 w-3" /> Back to client profile
      </button>

      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Financial MRI · Session</div>
          <h1 className="text-2xl md:text-3xl font-bold text-brand-indigo">{client.name}</h1>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {!canEdit && <span className="rounded-full bg-brand-indigo/10 text-brand-indigo font-semibold px-2 py-0.5">READ-ONLY VIEW</span>}
          {savedAt && canEdit && <span className="flex items-center gap-1 text-emerald-600"><Check className="h-3 w-3" /> Saved at {savedAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>}
        </div>
      </div>

      {/* Progress bar */}
      <div className="bg-white border border-brand-border rounded-xl p-3 md:p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-semibold text-brand-indigo">Step {step + 1} of {STEPS.length} · {STEPS[step].label}</div>
          <div className="text-xs text-muted-foreground">{Math.round(((step + 1) / STEPS.length) * 100)}%</div>
        </div>
        <div className="h-2 rounded-full bg-brand-bg overflow-hidden">
          <div className="h-full bg-gradient-to-r from-brand-indigo to-brand-cyan score-bar-fill" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
        </div>
        <div className="mt-3 flex gap-1 flex-wrap">
          {STEPS.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setStep(i)}
              className={`text-[10px] px-2 py-1 rounded-full font-semibold transition-colors ${
                i === step ? 'bg-brand-indigo text-white' : i < step ? 'bg-brand-green/15 text-brand-greenDark' : 'bg-brand-bg text-muted-foreground hover:bg-brand-border'
              }`}
            >
              {i + 1}. {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div>
          <div key={step} className="wizard-step-enter bg-white border border-brand-border rounded-xl p-5 md:p-8">
            {renderStep()}
          </div>

          {/* Conversation log below wizard */}
          <div className="mt-5 space-y-5">
            <PrefillLinkButton session={session} canEdit={canEdit} onUpdate={setSession} />
            <ConversationLog session={session} canEdit={canEdit} onUpdate={setSession} />
          </div>

          {canEdit ? (
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 no-print">
            <Button variant="outline" onClick={goBack} disabled={step === 0} className="border-brand-border"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleSaveDraft} className="border-brand-border text-brand-indigo"><Save className="h-4 w-4 mr-2" /> Save draft</Button>
              {step < STEPS.length - 1 ? (
                <Button onClick={goNext} className="bg-brand-indigo hover:bg-brand-indigoLight text-white">Next <ArrowRight className="h-4 w-4 ml-2" /></Button>
              ) : (
                <Button onClick={handleComplete} className="bg-brand-green hover:bg-brand-greenDark text-white">Complete &amp; Generate Report <Check className="h-4 w-4 ml-2" /></Button>
              )}
            </div>
          </div>
          ) : (
            <div className="mt-5 flex items-center justify-between gap-3 no-print">
              <Button variant="outline" onClick={() => nav(`/clients/${clientId}`)} className="border-brand-border"><ArrowLeft className="h-4 w-4 mr-2" /> Back to client</Button>
              <Button onClick={() => nav(`/clients/${clientId}/mri/${session.id}/report`)} className="bg-brand-indigo hover:bg-brand-indigoLight text-white">View report <ArrowRight className="h-4 w-4 ml-2" /></Button>
            </div>
          )}
        </div>

        {/* Desktop score panel */}
        <aside className="hidden lg:block sticky top-6 self-start">
          <ScorePanel scores={scores} />
        </aside>
      </div>

      {/* Mobile score sheet */}
      <div className="lg:hidden fixed bottom-4 right-4 z-30">
        <Sheet>
          <SheetTrigger asChild>
            <Button className="rounded-full shadow-lg bg-brand-indigo hover:bg-brand-indigoLight text-white h-14 px-5">
              <BarChart3 className="h-5 w-5 mr-2" /> Score {scores.overall}
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
            <div className="pt-2"><ScorePanel scores={scores} /></div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
};

export default MRIWizardPage;
